<template>
  <header :class="$style.header">
    <p :class="[$style.header__name, $style.name]">{{ userName }}</p>
    <div :class="[$style.header__action, $style.action]">
      <FlatButton
        size="small"
        variant="base"
        @click="matchAssignedPlannerAccountManagerModal.openModal"
      >
        担当者
      </FlatButton>
      <Only400FOA>
        <FlatButton
          size="small"
          variant="base"
          @click="requestFileUploadForOAModal.openModal"
        >
          ファイル（400F OA 限定）
        </FlatButton>
      </Only400FOA>
      <FlatButton size="small" variant="base" @click="memoModal.openModal">
        メモ（{{ memos.length }}件）
      </FlatButton>
      <FlatButton
        size="small"
        variant="base"
        @click="normalMessageChatReplyModal.openModal"
      >
        フリーワード自動応答
        <span
          v-if="normalMessageChatReplyDisabled"
          :class="$style.normalMessageChatReplyDisabled"
        >
          (停止中)
        </span>
        <span v-else>(利用中)</span>
      </FlatButton>
      <ChatRoomStatusSelector
        :class="[$style.header__selector, $style.selector]"
        :selectedStatus="status"
        @changeStatus="handleChatRoomStatus"
      />
    </div>

    <RequestFileUploadForOAModalControlledModal>
      <RequestFileUploadForOAModal
        :roomId="roomId"
        :user-name="userName"
        @close="requestFileUploadForOAModal.closeModal"
      />
    </RequestFileUploadForOAModalControlledModal>

    <MemoControlledModal>
      <MemoModal
        :memos="sortedMemos"
        :is-updating="isUpdatingMemos"
        @close="memoModal.closeModal"
        @create="content => $emit('createMemo', content)"
        @delete="id => $emit('deleteMemo', id)"
      />
    </MemoControlledModal>

    <MatchAssignedPlannerAccountManagerModalControlledModal>
      <MatchAssignedPlannerAccountManagerModal
        :chat-room-id="roomId"
        :is-team-administrator-member="isTeamAdministratorMember"
        @close="matchAssignedPlannerAccountManagerModal.closeModal"
        @updated-role="$emit('updatedRole')"
      />
    </MatchAssignedPlannerAccountManagerModalControlledModal>

    <NormalMessageChatReplyModalControlledModal>
      <NormalMessageChatReplyModal
        :roomId="roomId"
        :normalMessageChatReplyDisabled="normalMessageChatReplyDisabled"
        @submit="emit('updateNormalMessageChatReplyMatchSetting')"
        @close="normalMessageChatReplyModal.closeModal"
      />
    </NormalMessageChatReplyModalControlledModal>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ChatRoomStatusSelector from '../components/ChatRoomStatusSelector/ChatRoomStatusSelector.vue'
import { ChatRoomStatus } from '@/types/schema.generated'
import type { RoomID } from '@/types/app'
import { type ChatPageHeaderMemoFragment } from './ChatPageHeader.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import Only400FOA from '@/components/organisms/Only400FOA/Only400FOA.vue'
import { useModal } from '@/hooks/useModal'
import RequestFileUploadForOAModal from './RequestFileUploadForOAModal.vue'
import MemoModal from './MemoModal.vue'
import MatchAssignedPlannerAccountManagerModal from './MatchAssignedPlannerAccountManagerModal.vue'
import NormalMessageChatReplyModal from './NormalMessageChatReplyModal.vue'

const props = defineProps<{
  userName: string
  status: ChatRoomStatus
  roomId: RoomID
  memos: ChatPageHeaderMemoFragment[]
  isUpdatingMemos: boolean
  isTeamAdministratorMember: boolean
  normalMessageChatReplyDisabled: boolean
}>()

const emit = defineEmits<{
  (event: 'createMemo', content: string): void
  (event: 'deleteMemo', id: ChatPageHeaderMemoFragment['id']): void
  (event: 'updateStatus', status: ChatRoomStatus): void
  (event: 'updatedRole'): void
  (event: 'updateNormalMessageChatReplyMatchSetting'): void
}>()

const sortedMemos = computed(() => {
  // createdAtの新しい順に並べている
  // TODO: (@RyoNkmr) サーバ実装に対して暗黙的なのでsort()にしたほうが良いと思う

  // eslint-disable-next-line fp/no-mutating-methods
  return [...props.memos].reverse()
})

const memoModal = useModal()
const MemoControlledModal = memoModal.ControlledModal

const matchAssignedPlannerAccountManagerModal = useModal()
const MatchAssignedPlannerAccountManagerModalControlledModal =
  matchAssignedPlannerAccountManagerModal.ControlledModal
const requestFileUploadForOAModal = useModal()
const RequestFileUploadForOAModalControlledModal =
  requestFileUploadForOAModal.ControlledModal

const normalMessageChatReplyModal = useModal()
const NormalMessageChatReplyModalControlledModal =
  normalMessageChatReplyModal.ControlledModal

const handleChatRoomStatus = (status: ChatRoomStatus) => {
  emit('updateStatus', status)
}
</script>

<style lang="scss" module>
.header {
  display: flex;
  height: 52px;
  align-items: center;
  padding: 12px;
  background-color: $color-surface-primary;

  &__action {
    margin: 4px 16px 0 auto;
  }
}

.action {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.name {
  font-size: 14px;
  font-weight: bold;
  line-height: 24px;
}

.selector {
  width: 184px;
}

.normalMessageChatReplyDisabled {
  color: $color-text-and-object-accent-red;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}
</style>
